import React, { createContext, useContext, useState, useEffect } from 'react';
import type { ReactNode } from 'react';
import { PublicClientApplication } from '@azure/msal-browser';
import { isNative, platform } from '../capacitor';
import {
  oktaAuth,
  oktaAuthFor,
  getLoginTenant,
  setLoginTenant,
  clearLoginTenant,
  clearOktaTokens,
  isTenantConfigured,
  msalConfig,
  loginRequest,
} from '../authConfig';
import { identifyUser, captureError, clearBranchContext } from '../services/posthog';
import { AUTH_UNAUTHORIZED_EVENT, recoverOktaSession } from '../services/apiAuth';
import { isTestModeBypass, TEST_MODE_EMAIL, TEST_MODE_NAME } from '../services/testMode';
import { unregisterPushTokenOnLogout } from '../services/pushTokens';
// The cached record of who is signed in. Deliberately NOT importing authState's
// hasNativeMicrosoftSession: it answers from the implicit flow's localStorage token, which the
// code+PKCE flow replaced — nativeMsAuth is the only thing that knows where a native session
// lives now, and hasLiveNativeMicrosoftSession below is its equivalent.
import { AUTH_STATE_KEY, readCachedAuthState, cachedSessionProvider, type User } from '../services/authState';
import {
  clearNativeMicrosoftTokens,
  completeNativeMicrosoftLogin,
  hasLiveNativeMicrosoftSession,
  isNativeMicrosoftAuthResponse,
  startNativeMicrosoftLogin,
} from '../services/nativeMsAuth';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  loginWithOkta: () => void;
  loginWithMicrosoft: () => void;
  logout: () => void;
  refreshAuth: () => Promise<void>;
  completeLogin: (user: User) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Initialize MSAL
export const msalInstance = new PublicClientApplication(msalConfig);

// MSAL v5 requires initialize() to complete before any other API call (getAllAccounts,
// acquireTokenSilent, …). Memoize a single init promise so every caller — AuthProvider
// and the API clients in apiAuth — awaits the same one-time initialization rather than
// racing it. Idempotent and safe to await repeatedly.
let msalInitPromise: Promise<void> | null = null;
export function ensureMsalInitialized(): Promise<void> {
  if (!msalInitPromise) msalInitPromise = msalInstance.initialize();
  return msalInitPromise;
}

// Resolves once the initial auth determination has settled — i.e. AuthProvider has
// finished loading/renewing tokens (or confirmed there are none). The v2 API client
// (getV2AuthHeaders) awaits this before reading tokens, so a request fired from a
// component's mount effect parks until credentials are available rather than racing out
// with no Authorization header and drawing a spurious 401. A 5s safety cap guarantees the
// promise always resolves, so a stalled auth check can never hang every API call forever.
let resolveAuthReady: (() => void) | undefined;
const authReadyPromise = new Promise<void>((resolve) => {
  resolveAuthReady = resolve;
  setTimeout(resolve, 5000);
});
export function authReady(): Promise<void> {
  return authReadyPromise;
}
export function signalAuthReady(): void {
  resolveAuthReady?.();
}

// E2E test bypass only. Local development now uses the real Okta/Microsoft login flow (same
// as prod), so role + admin access resolve through the v2 backend exactly as they do in
// production — no mock dev user.
//
// The bypass guard lives in services/testMode so that every path which could drop the
// session — or issue a request on the bypass identity's behalf — consults one definition.
const TEST_MODE_USER: User = {
  name: TEST_MODE_NAME,
  email: TEST_MODE_EMAIL,
  provider: null,
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const cachedState = readCachedAuthState();
  const [user, setUser] = useState<User | null>(cachedState?.user || null);
  const [isAuthenticated, setIsAuthenticated] = useState(cachedState?.isAuthenticated || false);
  const [isLoading, setIsLoading] = useState(!cachedState);
  const [hasCheckedAuth, setHasCheckedAuth] = useState(!!cachedState);

  useEffect(() => {
    // A test-mode session is cached like any other, but there are no token stores to
    // reconcile it against — so it has to go through checkAuth's bypass rather than
    // reconciliation, which would find nothing and discard the session.
    if (cachedState?.isAuthenticated && !isTestModeBypass()) {
      // Optimistically render as authenticated from cache to avoid a login flash, but
      // reconcile that flag against the real token stores before trusting it. The cached
      // authState can outlive the MSAL/Okta tokens (storage partitioning/ITP eviction, a
      // logout in another tab), leaving the app "logged in" while holding no credentials, so
      // every v2 call fires token-less and 401s. Reconciliation also gates authReady(), so
      // parked API calls wait until we've confirmed a token actually exists rather than racing
      // out header-less.
      //
      // Note this only catches a session with NO cached account. An account that still exists
      // but whose token can no longer be renewed silently reconciles fine here and fails later
      // at acquisition — that case is handled in getV2AuthHeaders (WIPE-946).
      setIsLoading(false);
      setHasCheckedAuth(true);
      reconcileCachedSession();
      return;
    }
    checkAuth();
  }, []);

  // On Capacitor, Microsoft sign-in runs a manual OAuth2 implicit flow (see
  // services/nativeMsAuth) whose tokens never enter MSAL's account cache — the state was minted
  // there, not by MSAL. Those tokens are the only durable trace of that session, so
  // reconciliation has to consult them as a third token store. Without this a native session
  // looks credential-less on every reload and gets discarded, bouncing the user to the login
  // screen.
  //
  // Only a LIVE token counts. The implicit flow issues no refresh token, so an expired native
  // session holds nothing that can be renewed or sent; treating it as a session kept the app
  // rendering as signed in while every API call 401'd, with no way out but a manual sign-out.
  // Reporting it dead drops the user to the login screen, which is the one thing that recovers
  // it. A reload is the only path this covers — for a session that dies mid-use, the v2 API
  // client re-authenticates in place (see apiAuth.triggerNativeMicrosoftReauth).

  // Verify the cached authState against the actual MSAL/Okta token stores. If all are
  // empty the cached session is dead: clear it and drop to unauthenticated (renders the
  // login screen) instead of masquerading as signed-in. Always releases authReady() when
  // done so v2 calls parked in getV2AuthHeaders proceed.
  const reconcileCachedSession = async () => {
    try {
      await ensureMsalInitialized();
      // Consume any pending redirect (e.g. calendar Outlook connect) before reading accounts.
      await msalInstance.handleRedirectPromise();

      const msAccount = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0];
      let hasOkta = false;
      try {
        const tokens = await oktaAuth.tokenManager.getTokens();
        hasOkta = !!(tokens.accessToken || tokens.idToken);
      } catch {
        hasOkta = false;
      }

      if (!msAccount && !hasOkta && !(await hasLiveNativeMicrosoftSession())) {
        // Cached flag survived but the tokens are gone — the session is genuinely dead.
        clearAuthCache();
        clearOktaTokens();
        setUser(null);
        setIsAuthenticated(false);
        return;
      }

      // Keep the MSAL account active so silent renewal resolves it (see getV2AuthHeaders).
      if (msAccount) msalInstance.setActiveAccount(msAccount);
    } catch {
      // Reconciliation failed unexpectedly — keep the optimistic cached state rather than
      // logging the user out on a transient error.
    } finally {
      signalAuthReady();
    }
  };

  const cacheAuthState = (userData: User | null, authenticated: boolean) => {
    if (userData && authenticated) {
      identifyUser(userData.email, userData.name);
    }
    try {
      localStorage.setItem(AUTH_STATE_KEY, JSON.stringify({
        user: userData,
        isAuthenticated: authenticated
      }));
    } catch {
      // Ignore storage errors
    }
  };

  const clearAuthCache = () => {
    try {
      localStorage.removeItem(AUTH_STATE_KEY);
    } catch {
      // Ignore storage errors
    }
  };

  const checkAuth = async () => {
    // Only show loading on initial check
    if (!hasCheckedAuth) {
      setIsLoading(true);
    }

    try {
      if (isTestModeBypass()) {
        setUser(TEST_MODE_USER);
        setIsAuthenticated(true);
        cacheAuthState(TEST_MODE_USER, true);
        setIsLoading(false);
        setHasCheckedAuth(true);
        return;
      }

      // Initialize MSAL and handle any in-progress redirects
      await ensureMsalInitialized();

      // On Capacitor (iOS or Android): the app can boot straight onto the authorize response,
      // when the deep link back from Microsoft lands here rather than on /microsoft/callback.
      // Redeeming the code is nativeMsAuth's job; this only turns the result into a session.
      if (isNativeMicrosoftAuthResponse(window.location.hash)) {
        const claims = await completeNativeMicrosoftLogin(window.location.hash);
        window.location.hash = '';
        if (claims) {
          const msUser = { ...claims, provider: 'microsoft' as const };
          setUser(msUser);
          setIsAuthenticated(true);
          cacheAuthState(msUser, true);
          setIsLoading(false);
          setHasCheckedAuth(true);
          return;
        }
        // A response we couldn't prove was ours (stale or forged): fall through to the ordinary
        // checks, which find no session and render the login screen.
      }

      // Initialize MSAL and handle any in-progress redirects (web only)
      await ensureMsalInitialized();
      const redirectResult = await msalInstance.handleRedirectPromise();
      if (redirectResult?.account) {
        msalInstance.setActiveAccount(redirectResult.account);
        const msUser = {
          name: redirectResult.account.name || redirectResult.account.username,
          email: redirectResult.account.username,
          provider: 'microsoft' as const,
        };
        setUser(msUser);
        setIsAuthenticated(true);
        cacheAuthState(msUser, true);
        setIsLoading(false);
        setHasCheckedAuth(true);
        return;
      }

      // Check Okta auth
      const tokens = await oktaAuth.tokenManager.getTokens();

      if (tokens.idToken || tokens.accessToken) {
        try {
          const userInfo = await oktaAuth.getUser();
          const oktaUser = {
            name: userInfo.name || userInfo.email || 'User',
            email: userInfo.email || '',
            provider: 'okta' as const,
          };
          setUser(oktaUser);
          setIsAuthenticated(true);
          cacheAuthState(oktaUser, true);
          setIsLoading(false);
          setHasCheckedAuth(true);
          return;
        } catch {
          // Token may be expired — try to renew silently
          try {
            await oktaAuth.tokenManager.renew('idToken');
            await oktaAuth.tokenManager.renew('accessToken');
            const userInfo = await oktaAuth.getUser();
            const oktaUser = {
              name: userInfo.name || userInfo.email || 'User',
              email: userInfo.email || '',
              provider: 'okta' as const,
            };
            setUser(oktaUser);
            setIsAuthenticated(true);
            cacheAuthState(oktaUser, true);
            setIsLoading(false);
            setHasCheckedAuth(true);
            return;
          } catch {
            await oktaAuth.tokenManager.clear();
          }
        }
      }

      // Check Microsoft auth (already initialized above)
      const accounts = msalInstance.getAllAccounts();
      if (accounts.length > 0) {
        // Mark the account active so MSAL's silent renewal reliably resolves it and uses
        // the cached token / refresh token, instead of falling through to a hidden iframe.
        msalInstance.setActiveAccount(accounts[0]);
        const msUser = {
          name: accounts[0].name || accounts[0].username,
          email: accounts[0].username,
          provider: 'microsoft' as const,
        };
        setUser(msUser);
        setIsAuthenticated(true);
        cacheAuthState(msUser, true);
        setIsLoading(false);
        setHasCheckedAuth(true);
        return;
      }

      // No auth found
      setUser(null);
      setIsAuthenticated(false);
      clearAuthCache();
    } catch (error) {
      captureError(error, { component: 'AuthProvider.checkAuth', isNative, platform });
      setUser(null);
      setIsAuthenticated(false);
      clearAuthCache();
    } finally {
      setIsLoading(false);
      setHasCheckedAuth(true);
      // The initial determination has settled (signed in, renewed, or confirmed no
      // session) — release any v2 calls parked in getV2AuthHeaders.
      signalAuthReady();
    }
  };

  // The session is dead and nothing will revive it (see AUTH_UNAUTHORIZED_EVENT). Drop to the
  // unauthenticated state — clearing the cached auth state, any stale Okta tokens and a dead
  // Microsoft account so a remount can't resurrect the dead session — which renders LoginPage
  // for reauth. It is never fired for a 401 that merely raced auth init, so it can't log a healthy user out.
  useEffect(() => {
    const handleUnauthorized = () => {
      // MSAL's account cache is what checkAuth restores a Microsoft session from, so a dead one
      // left there would come back on the next reload. Only when Microsoft holds the session: a
      // user who also has a working Microsoft sign-in behind a dead Okta one keeps it.
      const endingMicrosoftSession = cachedSessionProvider() === 'microsoft';
      clearAuthCache();
      clearOktaTokens();
      if (endingMicrosoftSession) {
        // Local cache only — no sign-out from Entra, so a re-login can still reuse its session.
        ensureMsalInitialized().then(() => msalInstance.clearCache()).catch(() => {});
      }
      setUser(null);
      setIsAuthenticated(false);
    };
    window.addEventListener(AUTH_UNAUTHORIZED_EVENT, handleUnauthorized);
    return () => window.removeEventListener(AUTH_UNAUTHORIZED_EVENT, handleUnauthorized);
  }, []);

  // The Okta SDK renews tokens in the background and deletes any it fails to renew, so a
  // session that can no longer be renewed silently loses its tokens without the app being told.
  // Its `error` event is the only notice: recover interactively then, rather than letting the
  // UI stay up while the next v2 call goes out bare. If recovery is off the table, end the
  // session. `tokenKey` marks a renewal that actually ran and failed, as opposed to the SDK's
  // other errors (a renew requested with nothing to renew, a renew-loop guard). A refresh token
  // that survived the failure means Okta didn't reject it — the failure was transient, and the
  // next v2 call renews from it silently (see getV2AuthHeaders).
  //
  // Bound to the tenant the session was created against; a sign-in with the other tenant goes
  // through a redirect and remounts this. An unconfigured tenant (test builds) has no SDK to
  // listen to, and constructing one would throw.
  useEffect(() => {
    const tenant = getLoginTenant();
    if (!isTenantConfigured(tenant)) return;
    const tokenManager = oktaAuthFor(tenant).tokenManager;

    const handleRenewalError = (error: { tokenKey?: string }) => {
      if (!error?.tokenKey || cachedSessionProvider() !== 'okta') return;
      if (tokenManager.getTokensSync().refreshToken) return;
      if (!recoverOktaSession()) window.dispatchEvent(new Event(AUTH_UNAUTHORIZED_EVENT));
    };
    tokenManager.on('error', handleRenewalError);
    return () => tokenManager.off('error', handleRenewalError);
  }, []);

  // A sign-out in another tab — or that tab ending a dead session — removes the shared
  // authState along with the tokens, but this tab only learns of it through a storage event.
  // Follow it to the login screen; the other tab has already cleared the token stores. A null
  // key is a whole-storage clear.
  useEffect(() => {
    const handleStorage = (event: StorageEvent) => {
      if (event.storageArea !== localStorage) return;
      if (event.key !== null && event.key !== AUTH_STATE_KEY) return;
      if (readCachedAuthState()?.isAuthenticated) return;
      setUser(null);
      setIsAuthenticated(false);
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  // The button always goes to the primary tenant, which is now the new one. The old tenant
  // stays configured — session restore and logout resolve through the recorded marker, not
  // through this choice — and is reached only when Okta itself launches the app from that
  // tenant's tile; see OktaLogin.
  const loginWithOkta = () => {
    setLoginTenant('primary');
    oktaAuthFor('primary').signInWithRedirect();
  };

  const loginWithMicrosoft = async () => {
    if (isNative) {
      // On Capacitor, MSAL redirect/popup don't work with the WebView scheme, so native
      // sign-in runs the manual OAuth2 flow in services/nativeMsAuth — shared with the
      // re-authentication the v2 API client triggers when a native session expires, so both
      // produce a session the callback and the token readers understand.
      //
      // Everything there runs before the redirect, so an unguarded throw rejects
      // loginWithMicrosoft and the sign-in flow never starts — with no report and
      // nothing shown to the user. Mirror the MSAL path's handling instead.
      try {
        await startNativeMicrosoftLogin();
      } catch (error) {
        console.error('[MSAL] native loginWithMicrosoft error:', error);
        captureError(error, { component: 'AuthProvider.loginWithMicrosoft', isNative, platform });
        alert('Could not start Microsoft sign-in. Please try again.');
      }
      return;
    }

    try {
      await ensureMsalInitialized();
      try {
        await msalInstance.handleRedirectPromise();
      } catch (error) {
        // A redirect response MSAL couldn't redeem (an expired code: invalid_grant / AADSTS70008)
        // is no reason to abandon a fresh sign-in. MSAL memoizes the rejected promise for the
        // life of the page, so every press would rethrow it and never reach loginRedirect.
        // Report it, drop the local MSAL state it came from, and start over.
        captureError(error, { component: 'AuthProvider.loginWithMicrosoft.handleRedirectPromise', isNative, platform, errorCode: (error as { errorCode?: string })?.errorCode });
        await msalInstance.clearCache().catch(() => {});
      }
      await msalInstance.loginRedirect(loginRequest);
    } catch (error: any) {
      console.error('[MSAL] loginWithMicrosoft error:', error);
      captureError(error, { component: 'AuthProvider.loginWithMicrosoft', isNative, platform, errorCode: error?.errorCode });
      if (error?.errorCode === 'interaction_in_progress') {
        localStorage.clear();
        sessionStorage.clear();
        alert('Auth session was stuck. Please try again.');
      } else {
        alert('Could not start Microsoft sign-in. Please try again.');
      }
    }
  };

  const logout = async () => {
    // First, while the session's credentials can still authenticate the request: this device's
    // pushes would otherwise keep going to the user who has just signed out. Bounded, so a slow
    // backend can't hold up the sign-out.
    await unregisterPushTokenOnLogout();

    // Clear cached auth state
    clearAuthCache();

    // Drop the native session. MSAL holds no account for it, so logoutRedirect below can't reach
    // it — left behind, its refresh token would go on minting valid credentials after the
    // sign-out, and reconcileCachedSession would read it as a live session.
    await clearNativeMicrosoftTokens();

    // Clear PostHog branch context
    clearBranchContext();

    // Handle real auth providers
    if (user?.provider === 'okta') {
      const tenant = getLoginTenant();
      // signOut() only empties the store of the instance it runs on, and it navigates away, so
      // nothing after it executes: clear the other tenant and the marker first, or a leftover
      // token set signs the user straight back in on return.
      clearOktaTokens(tenant);
      clearLoginTenant();
      await oktaAuthFor(tenant).signOut();
    } else if (user?.provider === 'microsoft') {
      // MSAL is already initialized
      const accounts = msalInstance.getAllAccounts();
      if (accounts.length > 0) {
        await msalInstance.logoutRedirect({
          account: accounts[0],
        });
      }
    }
    setUser(null);
    setIsAuthenticated(false);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated,
        isLoading,
        loginWithOkta,
        loginWithMicrosoft,
        logout,
        refreshAuth: checkAuth,
        completeLogin: (u: User) => {
          setUser(u);
          setIsAuthenticated(true);
          setIsLoading(false);
          cacheAuthState(u, true);
        },
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
